import { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useAtomValue, useAtom } from 'jotai';
import { ThemeProvider as ThemeProviderStyledComponents  } from 'styled-components';
import { EUrlQueryParams } from 'common/enums/url-query-params.enums.ts';
import { themeNameAtom, themeSelectorAtom } from 'common/stores/theme.store.ts';
import { EThemes, EThemesSelector } from 'theme/enums/theme.enums.ts';
import { theme } from 'theme/theme.config.ts';

interface IProps {
  children: ReactNode;
}

const mql = window.matchMedia('(prefers-color-scheme: dark)');

const mqlAddEventListener = (onThemeChange: (e: MediaQueryListEvent) => void): void => {
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onThemeChange);
  } else if (typeof mql.addListener === 'function') {
    mql.addListener(onThemeChange);
  }
};

const mqlRemoveEventListener = (onThemeChange: (e: MediaQueryListEvent) => void): void => {
  if (typeof mql.removeEventListener === 'function') {
    mql.removeEventListener('change', onThemeChange);
  } else if (typeof mql.removeListener === 'function') {
    mql.removeListener(onThemeChange);
  }
};

const ThemeProvider = ({ children }: IProps): ReactElement => {
  const themeSelectorValue = useAtomValue(themeSelectorAtom);
  const [themeName, setThemeName] = useAtom(themeNameAtom);

  const changeThemeHandler = useCallback((e: MediaQueryListEvent): void => {
    setThemeName(e.matches ? EThemes.Dark : EThemes.Light);
  }, [setThemeName]);

  useEffect(() => {
    if (themeSelectorValue === EThemesSelector.Auto) {
      setThemeName(mql.matches ? EThemes.Dark : EThemes.Light);

      mqlAddEventListener(changeThemeHandler);
    } else {
      setThemeName(themeSelectorValue as unknown as EThemes);
      mqlRemoveEventListener(changeThemeHandler);
    }

    return () => {
      mqlRemoveEventListener(changeThemeHandler);
    };
  }, [changeThemeHandler, setThemeName, themeSelectorValue]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const theme = searchParams.get(EUrlQueryParams.Theme);

    if (theme && ['white', EThemes.Light, EThemes.Dark].includes(theme)) {
      setThemeName((theme === 'white' ? EThemes.Light : theme) as EThemes);
    }
  }, [setThemeName]);

  return (
    <ThemeProviderStyledComponents theme={{
      ...theme[themeName],
      themeName,
    }}>
      {children}
    </ThemeProviderStyledComponents>
  );
};

export default ThemeProvider;
